import { type ReactNode, useCallback, useEffect } from "react";
import { isEqual } from "lodash";
import { FormProvider, useForm } from "react-hook-form";
import { useRouteMatch } from "react-router-dom";
import { t } from "@/i18n-js/instance";
import { usePage } from "@/react/components/SettingsApp/Site/Pages/hooks/usePage";
import { useBuilderStore } from "@circle-react-shared/BuilderUI";
import { Icon } from "@circle-react-shared/Icon";
import { Button } from "@circle-react-shared/uikit/Button";
import { Dropdown } from "@circle-react-shared/uikit/Dropdown";
import type { CssVariables } from "../SiteThemes/constants";
import { CSS_VARIABLE_LOOKUP } from "./constants";
import { useFormActions } from "./hooks/useFormActions";

export interface FormData {
  theme: CssVariables;
}

interface FormWrapperProps {
  children: ReactNode;
}

export const FormWrapper = ({ children }: FormWrapperProps) => {
  const match = useRouteMatch<{ pageId: string }>(
    "/settings/pages/edit/:pageId",
  );
  const pageId = Number(match?.params.pageId);

  const { page } = usePage(pageId);

  const updateCurrentCanvasThemeVariables = useBuilderStore(
    state => state.updateCurrentCanvasThemeVariables,
  );
  const currentCanvasAppearance = useBuilderStore(state => state.appearance);
  const currentCanvasTheme = useBuilderStore(state => state.currentCanvasTheme);

  const pageThemeVariables = page?.theme.css_variables;
  const currentCanvasThemeVariables = currentCanvasTheme.cssVariables;

  const methods = useForm<FormData>({
    defaultValues: {
      theme: pageThemeVariables,
    },
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const { watch, getValues, setValue, reset } = methods;

  const {
    handleDiscard,
    handlePageThemUpdate,
    isPageLoading,
    handleSiteThemeUpdate,
  } = useFormActions({
    pageId,
    methods,
  });

  const processVariableUpdate = useCallback(
    (fieldValue: string, variableName: string) => {
      const variableLookup = CSS_VARIABLE_LOOKUP[variableName];

      if (!variableLookup) return;

      const key = variableLookup.category;
      const cssVariable = variableLookup.variable;

      if (!key || !cssVariable) return;

      if (key === "appearance") {
        updateCurrentCanvasThemeVariables(
          currentCanvasAppearance,
          cssVariable,
          fieldValue,
        );
      } else {
        updateCurrentCanvasThemeVariables(key, cssVariable, fieldValue);
      }
    },
    [updateCurrentCanvasThemeVariables, currentCanvasAppearance],
  );

  useEffect(() => {
    const subscription = watch((formData, { name }) => {
      if (!name || !formData.theme) return;

      const fieldValue = getValues(name);
      if (typeof fieldValue !== "string") return;

      processVariableUpdate(fieldValue, name);
    });

    return () => subscription.unsubscribe();
  }, [watch, processVariableUpdate, getValues]);

  useEffect(() => {
    reset();
  }, [reset, currentCanvasAppearance]);

  useEffect(() => {
    if (!isEqual(pageThemeVariables, currentCanvasThemeVariables)) {
      setValue("theme", currentCanvasThemeVariables, {
        shouldDirty: true,
      });
    }
  }, [
    setValue,
    currentCanvasThemeVariables,
    pageThemeVariables,
    currentCanvasAppearance,
  ]);

  return (
    <FormProvider {...methods}>
      <div className="flex h-[calc(100%-58px)] flex-col">
        <div className="h-full overflow-y-auto">{children}</div>
        <div className="bg-primary border-primary flex w-full justify-end gap-2 border-t px-6 py-3">
          <Button
            variant="secondary"
            onClick={handleDiscard}
            disabled={isPageLoading}
          >
            {t("builder_ui.theme_configuration.themes_list.discard")}
          </Button>
          <div className="flex space-x-px">
            <Button
              variant="circle"
              className="rounded-r-none"
              onClick={handlePageThemUpdate}
              disabled={isPageLoading}
            >
              {t("builder_ui.theme_configuration.update_page_theme")}
            </Button>
            <Dropdown
              popperOptions={{
                placement: "top-end",
                modifiers: [
                  {
                    name: "offset",
                    options: {
                      offset: [0, 10],
                    },
                  },
                ],
              }}
              button={
                <Button
                  variant="circle"
                  className="flex h-8 items-center justify-center rounded-l-none pl-1 pr-1.5"
                >
                  <Icon size={16} type="16-chevron-down" />
                </Button>
              }
            >
              <Dropdown.ItemWithLink onClick={handleSiteThemeUpdate}>
                {t("builder_ui.theme_configuration.update_site_theme")}
              </Dropdown.ItemWithLink>
            </Dropdown>
          </div>
        </div>
      </div>
    </FormProvider>
  );
};
